import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const IndicatorPanel = ({ indicators, onIndicatorToggle, ptsWizardParams, onParamChange }) => {
  const [activeCategory, setActiveCategory] = useState('trend');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const indicatorCategories = {
    trend: {
      label: 'Trend Indicators',
      icon: 'TrendingUp',
      indicators: [
        { id: 'sma20', name: 'SMA (20)', description: 'Simple Moving Average 20 periods' },
        { id: 'sma50', name: 'SMA (50)', description: 'Simple Moving Average 50 periods' },
        { id: 'ema21', name: 'EMA (21)', description: 'Exponential Moving Average 21 periods' },
        { id: 'bollinger', name: 'Bollinger Bands', description: 'Volatility indicator with upper/lower bands' },
        { id: 'ichimoku', name: 'Ichimoku Cloud', description: 'Comprehensive trend and momentum indicator' }
      ]
    },
    momentum: {
      label: 'Momentum Indicators',
      icon: 'Zap',
      indicators: [
        { id: 'rsi', name: 'RSI (14)', description: 'Relative Strength Index' },
        { id: 'macd', name: 'MACD', description: 'Moving Average Convergence Divergence' },
        { id: 'stochastic', name: 'Stochastic', description: 'Stochastic Oscillator' },
        { id: 'williams', name: 'Williams %R', description: 'Williams Percent Range' },
        { id: 'cci', name: 'CCI', description: 'Commodity Channel Index' }
      ]
    },
    volume: {
      label: 'Volume Indicators',
      icon: 'BarChart3',
      indicators: [
        { id: 'volume', name: 'Volume', description: 'Trading volume bars' },
        { id: 'vwap', name: 'VWAP', description: 'Volume Weighted Average Price' },
        { id: 'obv', name: 'OBV', description: 'On Balance Volume' },
        { id: 'mfi', name: 'MFI', description: 'Money Flow Index' },
        { id: 'ad', name: 'A/D Line', description: 'Accumulation/Distribution Line' }
      ]
    },
    support: {
      label: 'Support & Resistance',
      icon: 'Layers',
      indicators: [
        { id: 'pivot', name: 'Pivot Points', description: 'Daily pivot levels' },
        { id: 'fibonacci', name: 'Fibonacci', description: 'Fibonacci retracement levels' },
        { id: 'support', name: 'Support Levels', description: 'Automatic support detection' },
        { id: 'resistance', name: 'Resistance Levels', description: 'Automatic resistance detection' }
      ]
    }
  };

  const ptsWizardParameters = [
    { key: 'period1', label: 'Period 1', value: ptsWizardParams?.period1 || 10, type: 'number' },
    { key: 'period2', label: 'Period 2', value: ptsWizardParams?.period2 || 21, type: 'number' },
    { key: 'source', label: 'Source', value: ptsWizardParams?.source || 'hlc3', type: 'select', options: ['hlc3', 'close', 'open', 'high', 'low'] },
    { key: 'smoothing', label: 'Smoothing', value: ptsWizardParams?.smoothing || 3, type: 'number' },
    { key: 'threshold1', label: 'Threshold 1', value: ptsWizardParams?.threshold1 || 53, type: 'number' },
    { key: 'threshold2', label: 'Threshold 2', value: ptsWizardParams?.threshold2 || -53, type: 'number' },
    { key: 'overbought', label: 'Overbought', value: ptsWizardParams?.overbought || 70, type: 'number' },
    { key: 'oversold', label: 'Oversold', value: ptsWizardParams?.oversold || 30, type: 'number' },
    { key: 'multiplier', label: 'Multiplier', value: ptsWizardParams?.multiplier || 2.5, type: 'number', step: 0.1 },
    { key: 'fibonacci1', label: 'Fibonacci 1', value: ptsWizardParams?.fibonacci1 || 0.618, type: 'number', step: 0.001 },
    { key: 'fibonacci2', label: 'Fibonacci 2', value: ptsWizardParams?.fibonacci2 || 0.764, type: 'number', step: 0.001 }
  ];

  return (
    <div className={`bg-card border border-border rounded-lg transition-all duration-300 ${isCollapsed ? 'w-12' : 'w-80'}`}>
      {/* Panel Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        {!isCollapsed && (
          <h3 className="text-lg font-semibold text-foreground">Technical Indicators</h3>
        )}
        <Button variant="ghost" size="sm" onClick={() => setIsCollapsed(!isCollapsed)}>
          <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={16} />
        </Button>
      </div>
      {!isCollapsed && (
        <>
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-1 p-4 border-b border-border">
            {Object.entries(indicatorCategories)?.map(([key, category]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex items-center space-x-2 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                  activeCategory === key
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={category?.icon} size={14} />
                <span className="hidden sm:inline">{category?.label}</span>
              </button>
            ))}
          </div>

          {/* Indicators List */}
          <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
            {indicatorCategories?.[activeCategory]?.indicators?.map((indicator) => (
              <div key={indicator?.id} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={indicator?.id}
                      checked={indicators?.includes(indicator?.id)}
                      onChange={() => onIndicatorToggle(indicator?.id)}
                      className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                    />
                    <label htmlFor={indicator?.id} className="text-sm font-medium text-foreground cursor-pointer">
                      {indicator?.name}
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">{indicator?.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* PTS-WIZARD-V3 Parameters */}
          <div className="border-t border-border">
            <div className="p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2">
                <Icon name="Settings" size={16} />
                <span>PTS-WIZARD-V3 Parameters</span>
              </h4>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {ptsWizardParameters?.map((param) => (
                  <div key={param?.key}>
                    {param?.type === 'select' ? (
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          {param?.label}
                        </label>
                        <select
                          value={param?.value}
                          onChange={(e) => onParamChange(param?.key, e?.target?.value)}
                          className="w-full px-2 py-1 text-xs bg-input border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          {param?.options?.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <Input
                        label={param?.label}
                        type={param?.type}
                        value={param?.value}
                        onChange={(e) => onParamChange(param?.key, parseFloat(e?.target?.value) || 0)}
                        step={param?.step}
                        className="text-xs"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-t border-border">
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" className="flex-1">
                <Icon name="RotateCcw" size={14} />
                <span className="ml-1">Reset</span>
              </Button>
              <Button variant="default" size="sm" className="flex-1">
                <Icon name="Save" size={14} />
                <span className="ml-1">Save</span>
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default IndicatorPanel;